import {useCallback, useMemo, useState} from "react";
import {Photo} from "../hooks/useGalleryPhotos";
import {UNRELIABLE_TIME_SOURCES, TimeSource} from "../../../../utils/files/fileToDate";

export interface ReorderItem extends Photo {
  unreliable: boolean
}

const toItem = (p: Photo): ReorderItem => ({
  ...p,
  unreliable: !!p.time_source && UNRELIABLE_TIME_SOURCES.has(p.time_source as TimeSource),
})

const sortByTime = (a: Photo, b: Photo) => {
  const ta = Date.parse(a.sort_time); const tb = Date.parse(b.sort_time);
  if (isNaN(ta) && isNaN(tb)) return a.id - b.id;
  if (isNaN(ta)) return 1;
  if (isNaN(tb)) return -1;
  if (ta !== tb) return ta - tb;
  return a.id - b.id;
}

// Distribute new sort_time values evenly between `prevTime` (exclusive) and `nextTime` (exclusive).
// If either bound is unknown, pad with ±1 day per slot.
const ONE_DAY = 24 * 60 * 60 * 1000;
const ONE_MIN = 60 * 1000;
const formatSortTime = (ms: number) => {
  const d = new Date(ms);
  const pad = (n: number) => n < 10 ? "0" + n : "" + n;
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function useReorder(initialPhotos: Photo[]) {
  const [items, setItems] = useState<ReorderItem[]>(() => [...initialPhotos].sort(sortByTime).map(toItem))
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [selectMode, setSelectMode] = useState(false)
  const [lastClickedId, setLastClickedId] = useState<number | null>(null)

  const reset = useCallback((photos: Photo[]) => {
    setItems([...photos].sort(sortByTime).map(toItem))
    setSelected(new Set())
    setSelectMode(false)
    setLastClickedId(null)
  }, [])

  const toggle = useCallback((id: number) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
    setLastClickedId(id)
  }, [])

  const selectRange = useCallback((toId: number) => {
    setItems(currentItems => {
      const ids = currentItems.map(p => p.id)
      const toIdx = ids.indexOf(toId)
      const fromIdx = lastClickedId === null ? toIdx : ids.indexOf(lastClickedId)
      if (toIdx < 0 || fromIdx < 0) return currentItems
      const [a, b] = fromIdx <= toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx]
      setSelected(prev => {
        const next = new Set(prev)
        for (let i = a; i <= b; i++) next.add(ids[i])
        return next
      })
      return currentItems
    })
    setLastClickedId(toId)
  }, [lastClickedId])

  const clearSelection = useCallback(() => {
    setSelected(new Set())
    setSelectMode(false)
    setLastClickedId(null)
  }, [])

  // Reorder: move all currently-selected items to land *after* the item at index `targetIndex`
  // in the post-removal list. If targetIndex < 0, place at the start.
  // The dragged item itself doesn't matter — the selection is what moves.
  const moveSelectionTo = useCallback((targetIndex: number): {id: number, sort_time: string}[] => {
    if (selected.size === 0) return []

    const movingItems = items.filter(p => selected.has(p.id))
    const staying = items.filter(p => !selected.has(p.id))
    const safeTarget = Math.max(-1, Math.min(staying.length - 1, targetIndex))
    const before = staying.slice(0, safeTarget + 1)
    const after = staying.slice(safeTarget + 1)

    // Assign new sort_time values for the moved items, evenly spaced between the
    // unchanged neighbours.
    const prevNeighbor = before[before.length - 1]
    const nextNeighbor = after[0]
    const startMs = prevNeighbor ? Date.parse(prevNeighbor.sort_time) : NaN
    const endMs = nextNeighbor ? Date.parse(nextNeighbor.sort_time) : NaN

    let baseStart: number, baseEnd: number;
    if (!isNaN(startMs) && !isNaN(endMs)) {
      baseStart = startMs; baseEnd = endMs;
      if (baseEnd - baseStart < movingItems.length * 1000) {
        baseEnd = baseStart + Math.max(movingItems.length * 1000, ONE_MIN);
      }
    } else if (!isNaN(startMs)) {
      baseStart = startMs; baseEnd = startMs + ONE_DAY;
    } else if (!isNaN(endMs)) {
      baseStart = endMs - ONE_DAY; baseEnd = endMs;
    } else {
      baseStart = Date.now() - ONE_DAY; baseEnd = Date.now();
    }
    const step = (baseEnd - baseStart) / (movingItems.length + 1)

    const newSortTimeById = new Map<number, string>()
    movingItems.forEach((m, idx) => {
      newSortTimeById.set(m.id, formatSortTime(baseStart + step * (idx + 1)))
    })

    const movedWithTimes = movingItems.map(m => ({...m, sort_time: newSortTimeById.get(m.id)!}))
    const newItems = [...before, ...movedWithTimes, ...after]
    const changes = Array.from(newSortTimeById.entries()).map(([id, sort_time]) => ({id, sort_time}))

    setItems(newItems)
    return changes
  }, [selected, items])

  const indexById = useMemo(() => {
    const map = new Map<number, number>()
    items.forEach((p, i) => map.set(p.id, i))
    return map
  }, [items])

  return {
    items, selected, selectMode, setSelectMode,
    toggle, selectRange, clearSelection, reset,
    moveSelectionTo, indexById,
  }
}
